import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  apiErrorText,
  useGetGeoObjectsQuery,
  useGetRequestsQuery,
  useGetRequestsRegistryQuery,
  useCreateRequestMutation,
} from '@shared/api';
import {
  MTO_SUPPLY,
  REQUEST_TYPES,
  CREATABLE_REQUEST_TYPES,
  SOURCE_FUNCTIONS,
  PRIORITIES,
  TARGET_FUNCTIONS,
  REQUEST_STATUSES,
  functionLabel,
  requestTypeLabel,
  statusLabel,
  statusTone,
  priorityLabel,
  priorityTone,
  slaBadge,
  fmtRequestDate,
  isLegalRequest,
  allowedSourceFunctionsForType,
  type FunctionRequest,
} from '@entities/requests';
import {
  SupplyMaterialsEditor,
  SupplyObjectSelect,
  materialsForPayload,
  useSupplyObjectKind,
  type SupplyMaterialRow,
  type SupplyObject,
} from '@features/request-supply';
import { useAgentPermission } from '@entities/user';
import { useActiveProjectContext } from '../../shared/lib/activeProjectContext';
import { Card } from '@shared/ui/Card';
// Поля формы — общие, в тон полям заявки в снабжение (выбор объекта, материалы).
import { Input, Select as FormSelect, TextArea } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';

const Wrap = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const Title = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 720px;
`;

const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const Tab = styled.button<{ $active: boolean }>`
  font-size: 14px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  cursor: pointer;
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const Select = styled.select`
  font-family: inherit;
  font-size: 14px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FormCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: end;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const RowCard = styled(Card).attrs({ as: Link })`
  display: block;
  text-decoration: none;
  color: inherit;
  transition:
    border-color 0.12s ease,
    transform 0.12s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const RowHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const RowTitle = styled.div`
  font-size: 14.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const RowMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
  line-height: 1.5;
`;

const Flags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const CountsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13.5px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  height: 38px;
`;

/** Поле во всю ширину формы. */
const WideField = styled(Field)`
  grid-column: 1 / -1;
`;

const Wide = styled.div`
  grid-column: 1 / -1;
`;

const FormError = styled.div`
  grid-column: 1 / -1;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

/** Счётчики заявок по статусам (для сводки реестра, считаем на клиенте). */
function countsByStatus(rows: FunctionRequest[]): { status: string; n: number }[] {
  const m = new Map<string, number>();
  for (const r of rows) m.set(r.status, (m.get(r.status) ?? 0) + 1);
  return [...m.entries()].map(([status, n]) => ({ status, n }));
}

function RequestRow({ r }: { r: FunctionRequest }) {
  const sla = slaBadge(r);
  return (
    <RowCard to={`/requests/${r.id}`}>
      <RowHead>
        <RowTitle>{r.title}</RowTitle>
        <Badge $tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
      </RowHead>
      <RowMeta>
        {requestTypeLabel(r.request_type)} · {functionLabel(r.source_function)} →{' '}
        {functionLabel(r.target_function)}
        <br />
        Создана: {fmtRequestDate(r.created_at)}
        {r.due_date && <> · срок: {fmtRequestDate(r.due_date)}</>}
      </RowMeta>
      <Flags>
        <Badge $tone={priorityTone(r.priority)}>{priorityLabel(r.priority)}</Badge>
        {sla && <Badge $tone={sla.tone}>{sla.label}</Badge>}
        {r.result_ref && <Badge $tone="success">результат прикреплён</Badge>}
        {r.needs_review && <Badge $tone="warning">нужна проверка</Badge>}
      </Flags>
    </RowCard>
  );
}

const EMPTY_FORM = {
  request_type: 'aosr',
  source_function: 'field',
  title: '',
  priority: 'normal',
  due_date: '',
  project_id: '',
  site_id: '',
  element_id: '',
  comment: '',
  counterparty: '',
  inn: '',
};

const NO_OBJECT: SupplyObject = { projectId: '', siteId: '' };

/**
 * Реестр электронных заявок между функциями (`/requests`): вкладки «Мои»
 * (исходящие) и «Входящие» (очередь target-функции) + форма создания. Карточка
 * заявки — на `/requests/:id`.
 */
export function RequestsPage() {
  const { activeProjectId, setActiveProjectId } = useActiveProjectContext();
  const [box, setBox] = useState<'outgoing' | 'incoming' | 'registry'>('outgoing');
  const [targetFn, setTargetFn] = useState('pto_act');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, project_id: activeProjectId }));
  // Поля заявки в снабжение: объект и материалы списком.
  const [supplyObject, setSupplyObject] = useState<SupplyObject>(() =>
    activeProjectId ? { projectId: activeProjectId, siteId: '' } : NO_OBJECT,
  );
  const [materials, setMaterials] = useState<SupplyMaterialRow[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [reg, setReg] = useState({ function: '', type: '', status: '', overdue: false });
  const isSupplyForm = form.request_type === MTO_SUPPLY;
  const isLegalForm = isLegalRequest(form.request_type);
  const isPeoOrHrForm =
    form.request_type === 'budget_limit_exceeded' || form.request_type === 'staffing_request';
  const ptoAccess = useAgentPermission('pto');
  const fieldAccess = useAgentPermission('field');
  const supplyObjectKind = useSupplyObjectKind();
  const { data: geoObjects } = useGetGeoObjectsQuery(undefined, { skip: !showForm });

  const isRegistry = box === 'registry';
  const { data, isLoading, isError } = useGetRequestsQuery(
    box === 'incoming' ? { box, function: targetFn } : { box },
    { skip: isRegistry },
  );
  const {
    data: regData,
    isLoading: regLoading,
    isError: regError,
  } = useGetRequestsRegistryQuery(
    {
      function: reg.function || undefined,
      type: reg.type || undefined,
      status: reg.status || undefined,
      overdue: reg.overdue || undefined,
    },
    { skip: !isRegistry },
  );
  const [createRequest, { isLoading: creating }] = useCreateRequestMutation();

  const requests = (isRegistry ? regData?.requests : data?.requests) ?? [];
  const loading = isRegistry ? regLoading : isLoading;
  const errored = isRegistry ? regError : isError;

  const allowedInitiators = allowedSourceFunctionsForType(form.request_type);
  const availableSourceFunctions = SOURCE_FUNCTIONS.filter((s) =>
    allowedInitiators.includes(s.value),
  );

  const submit = async () => {
    if (!form.title.trim()) return;
    setFormError(null);
    if (isSupplyForm && !supplyObject.projectId && !supplyObject.siteId) {
      setFormError(
        supplyObjectKind === 'site'
          ? 'Выберите площадку, на которую нужны материалы.'
          : 'Выберите объект, на который нужны материалы.',
      );
      return;
    }
    try {
      if (isSupplyForm) {
        if (supplyObject.projectId) setActiveProjectId(supplyObject.projectId);
        await createRequest({
          request_type: form.request_type,
          source_function: form.source_function,
          title: form.title.trim(),
          priority: form.priority,
          due_date: form.due_date || undefined,
          project_id: supplyObject.projectId || undefined,
          site_id: supplyObject.siteId || undefined,
          payload: {
            materials: materialsForPayload(materials),
            ...(form.comment.trim() ? { comment: form.comment.trim() } : {}),
            ...(form.due_date ? { need_by: form.due_date } : {}),
          },
          submit: true,
        }).unwrap();
      } else if (isLegalForm) {
        if (form.project_id) setActiveProjectId(form.project_id);
        await createRequest({
          request_type: form.request_type,
          source_function: form.source_function,
          title: form.title.trim(),
          priority: form.priority,
          due_date: form.due_date || undefined,
          project_id: form.project_id || undefined,
          site_id: form.site_id || undefined,
          payload: {
            ...(form.counterparty.trim() ? { counterparty: form.counterparty.trim() } : {}),
            ...(form.inn.trim() ? { inn: form.inn.trim() } : {}),
            ...(form.comment.trim() ? { comment: form.comment.trim() } : {}),
          },
          submit: true,
        }).unwrap();
      } else if (isPeoOrHrForm) {
        if (form.project_id) setActiveProjectId(form.project_id);
        await createRequest({
          request_type: form.request_type,
          source_function: form.source_function,
          title: form.title.trim(),
          priority: form.priority,
          due_date: form.due_date || undefined,
          project_id: form.project_id || undefined,
          site_id: form.site_id || undefined,
          payload: {
            ...(form.comment.trim() ? { comment: form.comment.trim() } : {}),
          },
          submit: true,
        }).unwrap();
      } else {
        if (form.project_id) setActiveProjectId(form.project_id);
        await createRequest({
          request_type: form.request_type,
          source_function: form.source_function,
          title: form.title.trim(),
          priority: form.priority,
          due_date: form.due_date || undefined,
          project_id: form.project_id || undefined,
          site_id: form.site_id || undefined,
          element_id: form.element_id || undefined,
        }).unwrap();
      }
    } catch (e) {
      setFormError(apiErrorText(e, 'Заявка не создана.'));
      return;
    }
    setForm({ ...EMPTY_FORM, project_id: activeProjectId });
    setSupplyObject(activeProjectId ? { projectId: activeProjectId, siteId: '' } : NO_OBJECT);
    setMaterials([]);
    setShowForm(false);
    setBox('outgoing');
  };

  const set = (k: keyof typeof form, v: string) =>
    setForm((f) => {
      if (k === 'request_type') {
        const nextAllowed = allowedSourceFunctionsForType(v);
        const nextSource = nextAllowed.includes(f.source_function)
          ? f.source_function
          : (nextAllowed[0] ?? f.source_function);
        return { ...f, request_type: v, source_function: nextSource };
      }
      return { ...f, [k]: v };
    });

  return (
    <Wrap>
      <Header>
        <Title>
          Заявки <span className="accent">между функциями</span>
        </Title>
        <Subtitle>
          Сквозной обмен заявками между функциями платформы: освидетельствование (АОСР), геодезия,
          лаборатория, неразрушающий контроль, снабжение материалами, ПЭО, кадры и юрслужба.
        </Subtitle>
      </Header>

      <Tabs>
        <Tab $active={box === 'outgoing'} onClick={() => setBox('outgoing')}>
          Мои
        </Tab>
        <Tab $active={box === 'incoming'} onClick={() => setBox('incoming')}>
          Входящие
        </Tab>
        <Tab $active={box === 'registry'} onClick={() => setBox('registry')}>
          Сводный
        </Tab>
      </Tabs>

      <Bar>
        {box === 'incoming' && (
          <Field>
            Функция
            <Select value={targetFn} onChange={(e) => setTargetFn(e.target.value)}>
              {TARGET_FUNCTIONS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {isRegistry && (
          <>
            <Field>
              Функция
              <Select
                value={reg.function}
                onChange={(e) => setReg((s) => ({ ...s, function: e.target.value }))}
              >
                <option value="">все</option>
                {TARGET_FUNCTIONS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              Тип
              <Select
                value={reg.type}
                onChange={(e) => setReg((s) => ({ ...s, type: e.target.value }))}
              >
                <option value="">все</option>
                {REQUEST_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              Статус
              <Select
                value={reg.status}
                onChange={(e) => setReg((s) => ({ ...s, status: e.target.value }))}
              >
                <option value="">все</option>
                {REQUEST_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </Field>
            <CheckRow>
              <input
                type="checkbox"
                checked={reg.overdue}
                onChange={(e) => setReg((s) => ({ ...s, overdue: e.target.checked }))}
              />
              только просроченные
            </CheckRow>
          </>
        )}
        <div style={{ marginLeft: 'auto' }}>
          {!isRegistry && (
            <Button
              $variant={showForm ? 'ghost' : 'primary'}
              onClick={() => {
                if (!showForm) {
                  setForm({
                    ...EMPTY_FORM,
                    project_id: activeProjectId,
                    source_function:
                      !fieldAccess.can_write && ptoAccess.can_write ? 'pto' : 'field',
                  });
                  if (activeProjectId) {
                    setSupplyObject({ projectId: activeProjectId, siteId: '' });
                  }
                  setFormError(null);
                }
                setShowForm((v) => !v);
              }}
            >
              {showForm ? 'Отмена' : 'Новая заявка'}
            </Button>
          )}
        </div>
      </Bar>

      {showForm && !isRegistry && (
        <FormCard>
          <Field>
            Тип
            <FormSelect
              value={form.request_type}
              onChange={(e) => set('request_type', e.target.value)}
            >
              {CREATABLE_REQUEST_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </FormSelect>
          </Field>
          <Field>
            Инициатор
            <FormSelect
              value={form.source_function}
              onChange={(e) => set('source_function', e.target.value)}
            >
              {availableSourceFunctions.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </FormSelect>
          </Field>
          <WideField>
            Наименование
            <Input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder={
                isSupplyForm
                  ? 'Что нужно: «Арматура на фундаменты Фм-1»'
                  : isLegalForm
                    ? 'Предмет запроса: «Согласование ДС №2 к договору подряда с ООО СтройТех»'
                    : isPeoOrHrForm
                      ? 'Краткая суть запроса (обоснование лимита / потребность в персонале)'
                      : 'Что освидетельствуем / проверяем'
              }
            />
          </WideField>
          {isSupplyForm && (
            <Field>
              {supplyObjectKind === 'site' ? 'Площадка (обязательно)' : 'Объект (обязательно)'}
              <SupplyObjectSelect value={supplyObject} onChange={setSupplyObject} />
            </Field>
          )}
          <Field>
            Приоритет
            <FormSelect value={form.priority} onChange={(e) => set('priority', e.target.value)}>
              {PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </FormSelect>
          </Field>
          <Field>
            {isSupplyForm ? 'Нужно к' : 'Срок'}
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => set('due_date', e.target.value)}
            />
          </Field>
          {isSupplyForm ? (
            <>
              <Wide>
                <SupplyMaterialsEditor rows={materials} onChange={setMaterials} />
              </Wide>
              <WideField>
                Комментарий (необязательно)
                <TextArea
                  value={form.comment}
                  onChange={(e) => set('comment', e.target.value)}
                  placeholder="Что важно снабжению: куда везти, что можно заменить, кому звонить"
                  maxLength={4000}
                />
              </WideField>
            </>
          ) : isLegalForm ? (
            <>
              <Field>
                Контрагент / адресат
                <Input
                  value={form.counterparty}
                  onChange={(e) => set('counterparty', e.target.value)}
                  placeholder="ООО «СтройТех»"
                />
              </Field>
              <Field>
                ИНН контрагента (необязательно)
                <Input
                  value={form.inn}
                  onChange={(e) => set('inn', e.target.value)}
                  placeholder="10 или 12 цифр"
                />
              </Field>
              <Field>
                Объект (проект ПТО)
                <FormSelect
                  value={form.project_id}
                  onChange={(e) => set('project_id', e.target.value)}
                >
                  <option value="">— не выбран —</option>
                  {(geoObjects?.projects ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </FormSelect>
              </Field>
              <WideField>
                Суть вопроса / фактура для юристов
                <TextArea
                  value={form.comment}
                  onChange={(e) => set('comment', e.target.value)}
                  placeholder="Опишите суть вопроса, реквизиты договора, обстоятельства нарушения или полномочия для доверенности"
                  maxLength={4000}
                />
              </WideField>
            </>
          ) : isPeoOrHrForm ? (
            <>
              <Field>
                Объект (проект ПТО)
                <FormSelect
                  value={form.project_id}
                  onChange={(e) => set('project_id', e.target.value)}
                >
                  <option value="">— не выбран —</option>
                  {(geoObjects?.projects ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </FormSelect>
              </Field>
              <Field>
                Площадка
                <FormSelect value={form.site_id} onChange={(e) => set('site_id', e.target.value)}>
                  <option value="">— не выбрана —</option>
                  {(geoObjects?.sites ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </FormSelect>
              </Field>
              <WideField>
                Обоснование / детали запроса
                <TextArea
                  value={form.comment}
                  onChange={(e) => set('comment', e.target.value)}
                  placeholder="Укажите детали превышения лимита (статья, сумма, причина) или требуемые специальности и численность"
                  maxLength={4000}
                />
              </WideField>
            </>
          ) : (
            <>
              <Field>
                Объект (проект ПТО)
                <FormSelect
                  value={form.project_id}
                  onChange={(e) => set('project_id', e.target.value)}
                >
                  <option value="">— не выбран —</option>
                  {(geoObjects?.projects ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </FormSelect>
              </Field>
              <Field>
                Площадка
                <FormSelect value={form.site_id} onChange={(e) => set('site_id', e.target.value)}>
                  <option value="">— не выбрана —</option>
                  {(geoObjects?.sites ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </FormSelect>
              </Field>
              <Field>
                Конструктивный элемент
                <FormSelect
                  value={form.element_id}
                  onChange={(e) => {
                    const id = e.target.value;
                    const el = (geoObjects?.elements ?? []).find((x) => x.id === id);
                    setForm((f) => ({
                      ...f,
                      element_id: id,
                      site_id: f.site_id || el?.site_id || '',
                      project_id: f.project_id || el?.project_id || '',
                    }));
                  }}
                >
                  <option value="">— не выбран —</option>
                  {(geoObjects?.elements ?? [])
                    .filter(
                      (el) =>
                        (!form.site_id || el.site_id === form.site_id) &&
                        (!form.project_id || !el.project_id || el.project_id === form.project_id),
                    )
                    .map((el) => (
                      <option key={el.id} value={el.id}>
                        {el.code ? `${el.code} — ${el.title}` : el.title}
                      </option>
                    ))}
                </FormSelect>
              </Field>
            </>
          )}
          {formError && <FormError role="alert">{formError}</FormError>}
          <div>
            <Button
              $variant="primary"
              onClick={() => void submit()}
              disabled={creating || !form.title.trim()}
            >
              {creating
                ? isSupplyForm || isLegalForm || isPeoOrHrForm
                  ? 'Отправка…'
                  : 'Создание…'
                : isSupplyForm
                  ? 'Отправить в снабжение'
                  : isLegalForm
                    ? 'Направить в юрслужбу'
                    : form.request_type === 'budget_limit_exceeded'
                      ? 'Направить в ПЭО'
                      : form.request_type === 'staffing_request'
                        ? 'Направить в Кадры'
                        : 'Создать (черновик)'}
            </Button>
          </div>
        </FormCard>
      )}

      {isRegistry && !loading && !errored && requests.length > 0 && (
        <CountsBar>
          {countsByStatus(requests).map((c) => (
            <Badge key={c.status} $tone={statusTone(c.status)}>
              {statusLabel(c.status)}: {c.n}
            </Badge>
          ))}
        </CountsBar>
      )}

      {loading && <Muted>Загрузка заявок…</Muted>}
      {errored && (
        <Muted>
          {isRegistry
            ? 'Сводный реестр доступен координаторам (сквозной доступ ПТО/Прораб).'
            : 'Не удалось загрузить заявки (нет доступа к функции?).'}
        </Muted>
      )}
      {!loading && !errored && requests.length === 0 && (
        <Muted>
          {isRegistry
            ? 'Заявок по выбранным фильтрам нет.'
            : box === 'outgoing'
              ? 'У вас пока нет заявок.'
              : 'Входящих заявок нет.'}
        </Muted>
      )}
      {requests.length > 0 && (
        <List>
          {requests.map((r) => (
            <RequestRow key={r.id} r={r} />
          ))}
        </List>
      )}
    </Wrap>
  );
}
